import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useFormState } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  type AcceptQuotePayload,
  useCurrentMove,
  useSelectedQuote,
} from '../../../modules/moves'
import { type PaymentRequest } from '../../../modules/payment'
import Form, {
  SubmitButton,
} from '../../Form'

import SelectedQuote from '../../../pages/move/CheckoutPage/SelectedQuote'
import PromoCodeForm from '../PromoCodeForm'
import MoveDepositHeader from '../PaymentForm/MoveDepositHeader'
import { useConstraintsWarnings } from './useConstraintsWarnings'
import { useGetDefaultTime } from './useDefaultTime'
import { formatFormOutput } from './formDataBuilder'
import { type CheckoutFormValues } from './CheckoutForm.type'
import useValidationSchema from './validationSchema'
import NotesSection from './sections/NotesSection'
import ConditionsSection from './sections/ConditionsSection'
import DateAndTimeSection from './sections/DateAndTimeSection'
import DateRangesSection from './sections/DateRangesSection'
import QuoteDetails from '../../QuoteComponent/standalone/QuoteDetails'

type CheckoutFormProps = {
  onSubmit: (values: AcceptQuotePayload) => Promise<void> | void
  onPromoCodeSubmit?: (promoCode: string | null) => Promise<void> | void
  paymentField?: React.ReactNode
  paymentRequest?: PaymentRequest
  hasValidPaymentField?: boolean
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  onSubmit,
  onPromoCodeSubmit,
  paymentField,
  paymentRequest,
  hasValidPaymentField = true,
}) => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const selectedQuote = useSelectedQuote()
  const constraintsWarnings = useConstraintsWarnings()
  const hasConstraintsWarnings = Object.keys(constraintsWarnings).length > 0

  const getDefaultTime = useGetDefaultTime()

  const { isLongDistance } = move.state

  const defaultTime = selectedQuote?.jobs.map((jobQuote) => {
    const job = move.jobs.find((job) => job.id === jobQuote.jobId)
    const preferredTimeRanges = job?.dateTime?.preferredTimeRanges ?? []
    return getDefaultTime(
      selectedQuote?.companyBranch.arrivalWindows ?? [],
      job?.dateTime?.selectedTimeRange ? [job?.dateTime?.selectedTimeRange] : preferredTimeRanges,
    )
  })

  const [uploading, setUploading] = useState(false)

  const schema = useValidationSchema({ hasConstraintsWarnings })
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      selectedTime: defaultTime,
      // origin: move.origin,
      // destination: move.destination,
      note: move.notes?.fromCustomer?.note,
    },
  })

  const paymentFieldName = '_payment'
  const { submitCount } = useFormState({ control: form.control })

  const handleSubmit = async (data: CheckoutFormValues) => {
    await onSubmit(formatFormOutput(data, { isLongDistance }))
  }

  /**
   * revalidate the form when constraints are updated
   */
  useEffect(() => {
    if (submitCount > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      form.trigger()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConstraintsWarnings])

  /**
   * set form error when there's a payment field is invalid
   */
  useEffect(() => {
    if (submitCount === 0) {
      return
    }
    if (hasValidPaymentField) {
      form.clearErrors(paymentFieldName)
    } else {
      form.setError(paymentFieldName, {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidPaymentField, submitCount])

  if (!selectedQuote) {
    return null
  }

  return (
    <>
      <div className="hidden xl:flex">
        <SelectedQuote />
      </div>

      <div className="py-4">
        <QuoteDetails
          quote={selectedQuote}
          options={{
            promoCode: move.promoCode.appliedPromoCode ?? move.promoCode.pendingPromoCode,
            darkBackground: true,
            docTop: 70,
          }}
        />
      </div>

      { onPromoCodeSubmit && (
        <PromoCodeForm onSubmit={onPromoCodeSubmit} />
      ) }

      <Form form={form} onSubmit={handleSubmit}>

        { paymentField && paymentRequest && (
          <div className="my-4 flex flex-col justify-between gap-2 rounded-xl border bg-white p-4 text-neutral-500 lg:p-8">
            <MoveDepositHeader paymentRequest={paymentRequest} />
            { paymentField }
          </div>
        ) }

        <div className="my-4 rounded-xl border bg-white p-4 lg:p-8">

          { isLongDistance
            ? (
              <DateRangesSection
                move={move}
              />
              )
            : (
              <DateAndTimeSection
                move={move}
              />
              ) }

          <NotesSection
            move={move}
            onUploading={setUploading}
          />
        </div>

        <ConditionsSection
          move={move}
        />

        <div className="mb-6 mt-8 flex w-full justify-end sm:w-auto xl:mb-0 xl:mt-6">

          { uploading
            ? (
              <SubmitButton
                className="w-full sm:w-auto"
                disabled
              >
                { t('actions.bookMyMove') }
              </SubmitButton>
              )
            : (
              <SubmitButton
                className="w-full sm:w-auto"
              >
                { t('actions.bookMyMove') }
              </SubmitButton>
              ) }
        </div>
      </Form>
    </>
  )
}

export default CheckoutForm
